import { router } from '@inertiajs/react'
import {
    Box,
    Typography,
    Alert,
    Chip,
    Grid,
    Avatar,
    Divider
} from '@mui/material'
import {
    Edit as EditIcon,
    Visibility as PreviewIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon
} from '@mui/icons-material'
import { CustomCard, SecondaryButton, PrimaryButton } from '../index'
import me from '../../assets/me.png'
import { useAuth } from '../../hooks/useAuth'

interface ProfileData {
    // Basic Info
    first_name: string;
    last_initial: string;
    profile_pic_url: string | null;
    short_intro: string;

    // Employment
    occupation: string;
    employer: string;

    // Rental Preferences
    budget: string;
    bedrooms: string;
    bathrooms: string;
    desired_move_in: string;
    lease_length: string;
    rental_type?: string;
    tour_preference?: string;
    occupants: string;
    neighborhoods: string;
    additionalInfo: string;

    // Pet and housing info
    housingVoucher: string;
    numDogs: string;
    numCats: string;
}

interface ProfileTabProps {
    profileData: ProfileData;
}

export default function ProfileTab({ profileData }: ProfileTabProps) {
    const { renter } = useAuth();

    // Calculate profile completion
    const requiredFields = [
        'first_name',
        'short_intro',
        'bedrooms',
        'bathrooms',
        'desired_move_in',
        'lease_length',
        'occupants'
    ]

    const completedFields = requiredFields.filter(field => {
        const value = profileData[field as keyof typeof profileData]
        return value && value.toString().trim() !== ''
    })

    const completionPercentage = Math.round((completedFields.length / requiredFields.length) * 100)
    const isProfileComplete = completionPercentage === 100

    const handlePreviewProfile = () => {
        router.visit(`/renters/${renter?.id}/profile-preview`)
    }

    const handleEditProfile = () => {
        router.visit(`/renters/${renter?.id}/profile/edit`)
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body1" color="grey.600">
                    Manage your profile information and preferences
                </Typography>
            </Box>

            {/* Profile Completion Alert */}
            {!isProfileComplete && (
                <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{ mb: 3 }}
                    action={
                        <SecondaryButton
                            size="small"
                            onClick={handleEditProfile}
                        >
                            Complete Profile
                        </SecondaryButton>
                    }
                >
                    Your profile is {completionPercentage}% complete
                </Alert>
            )}

            {isProfileComplete && (
                <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 3 }}
                >
                    Your profile is complete! 🎉
                </Alert>
            )}

            {/* Profile Overview Card */}
            <CustomCard>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar
                            src={profileData.profile_pic_url || undefined}
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: 'grey.300',
                                fontSize: '2rem',
                                mr: 3
                            }}
                        >
                            {!profileData.profile_pic_url && (profileData.first_name?.[0] || '?')}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {profileData.first_name} {profileData.last_initial}.
                            </Typography>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                {profileData.occupation || 'Occupation not specified'}
                            </Typography>
                            <Chip
                                label={`${completionPercentage}% Complete`}
                                color={isProfileComplete ? 'success' : 'warning'}
                                size="small"
                            />
                        </Box>
                    </Box>

                    {profileData.short_intro && (
                        <>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {profileData.short_intro}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                        </>
                    )}

                    {/* Quick Stats */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="grey.600">
                                Budget
                            </Typography>
                            <Typography variant="h6">
                                ${profileData.budget}/month
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="grey.600">
                                Rental Type
                            </Typography>
                            <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                                {profileData.rental_type ? profileData.rental_type.replace('_', ' ') : 'Not specified'}
                            </Typography>
                        </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="grey.600">
                            Tour Preference
                        </Typography>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                            {profileData.tour_preference ? profileData.tour_preference.replace('_', ' ') : 'Not specified'}
                        </Typography>
                    </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="grey.600">
                                Move-in Date
                            </Typography>
                            <Typography variant="h6">
                                {profileData.desired_move_in ? new Date(profileData.desired_move_in).toLocaleDateString() : 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="grey.600">
                                Bedrooms
                            </Typography>
                            <Typography variant="h6">
                                {profileData.bedrooms || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="grey.600">
                                Bathrooms
                            </Typography>
                            <Typography variant="h6">
                                {profileData.bathrooms || 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600">
                                Lease Length
                            </Typography>
                            <Typography variant="body1">
                                {profileData.lease_length || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600">
                                Occupants
                            </Typography>
                            <Typography variant="body1">
                                {profileData.occupants ? `${profileData.occupants} person(s)` : 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600">
                                Housing Voucher
                            </Typography>
                            <Typography variant="body1">
                                {profileData.housingVoucher === 'yes' ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600">
                                Dogs
                            </Typography>
                            <Typography variant="body1">
                                {profileData.numDogs || '0'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600">
                                Cats
                            </Typography>
                            <Typography variant="body1">
                                {profileData.numCats || '0'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="grey.600">
                                Preferred Neighborhoods
                            </Typography>
                            <Typography variant="body1">
                                {profileData.neighborhoods || 'Not specified'}
                            </Typography>
                        </Grid>
                        {profileData.additionalInfo && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="grey.600">
                                    Additional Notes
                                </Typography>
                                <Typography variant="body1">
                                    {profileData.additionalInfo}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <SecondaryButton
                            startIcon={<EditIcon />}
                            onClick={handleEditProfile}
                            sx={{ flex: 1, minWidth: '200px' }}
                        >
                            Edit Profile
                        </SecondaryButton>
                        <PrimaryButton
                            startIcon={<PreviewIcon />}
                            onClick={handlePreviewProfile}
                            sx={{
                                flex: 1,
                                minWidth: '200px',
                                bgcolor: 'black',
                                '&:hover': { bgcolor: 'grey.800' }
                            }}
                        >
                            Preview Profile
                        </PrimaryButton>
                    </Box>
                </Box>
            </CustomCard>

        </Box>
    )
}
